import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class SharedApiService {
  private baseUrl = environment.URL;
  private apiUrl  = environment.API_URL;

  constructor(private http: HttpClient) {}

  // ── Auth (authenticated) ──────────────────────────────────────────────────────

  /** PATCH /api/auth/logout */
  logout(data: any): Observable<any> {
    return this.http.patch(`${this.apiUrl}auth/logout`, data);
  }

  /** POST /api/auth/logout-all-sessions */
  logoutAllSessions(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}auth/logout-all-sessions`, data);
  }

  /** PATCH /api/auth/change-password */
  changePassword(data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}api/auth/change-password`, data);
  }

  // ── User (authenticated) ──────────────────────────────────────────────────────

  /** GET /api/user/me */
  getMe(): Observable<any> {
    return this.http.get(`${this.apiUrl}user/me`);
  }

  /** GET /api/user/salesman */
  getSalesman(): Observable<any> {
    return this.http.get(`${this.apiUrl}user/salesman`);
  }

  /** PATCH /api/user/update — update profile for buyer & seller */
  updateUser(data: any): Observable<any> {
    return this.http.patch(`${this.apiUrl}user/update`, data);
  }

  /** POST /api/user/send-otp */
  userSendOtp(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}user/send-otp`, data);
  }

  /** POST /api/user/verify-otp */
  userVerifyOtp(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}auth/verify-otp`, data, {
      headers: new HttpHeaders({ 'X-Skip-Global-Error': 'true' })
    });
  }

  /** DELETE /api/user/remove/:id */
  deleteUser(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}user/remove/${id}`);
  }

  // ── File Uploads ──────────────────────────────────────────────────────────────

  /** POST /api/public/global-uploader — general-purpose file upload */
  globalFileUpload(formData: FormData): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.apiUrl}upload/global-uploader`, formData, { headers });
  }

  /** POST /api/upload/global-uploader-resume — resume file upload */
  uploadResumeFile(formData: FormData): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.apiUrl}upload/global-uploader-resume`, formData, { headers });
  }

  /** POST upload/public-image-uploader — no auth, guest-usable. Field 'files' (repeat per file), max 6 files/5MB each, images only. */
  uploadPublicImage(formData: FormData): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.baseUrl}upload/public-image-uploader`, formData, { headers });
  }

  // ── File Upload ───────────────────────────────────────────────────────────────

  /** POST upload/file — general file upload */
  uploadFile(file: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.apiUrl}upload/file`, file, { headers });
  }

  /** POST upload/image */
  uploadImage(file: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.baseUrl}upload/image`, file, { headers });
  }

  /** POST upload/video */
  uploadVideo(file: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.baseUrl}upload/video`, file, { headers });
  }

  /** POST upload/pdf */
  uploadPdf(file: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.baseUrl}upload/pdf`, file, { headers });
  }

  // ── Shared / Misc ─────────────────────────────────────────────────────────────

  /** POST common/subscribe */
  subscribe(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}common/subscribe`, data);
  }

  /** POST reciept/dayend-report */
  dayEndReport(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}reciept/dayend-report`, data);
  }

  /** POST comment/get-comment-hierarchy */
  getCommentHierarchy(referenceName: string): Observable<any> {
    return this.http.get(`${this.apiUrl}comment/get-comment-hierarchy`, { params: { referenceName } });
  }

  /** GET master/warehouselist */
  getWarehouseList(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}master/warehouselist`, data);
  }

  /** POST master/sidebarMenuAcess */
  getSidebarMenuAccess(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}master/sidebarMenuAcess`, data);
  }

  /** POST master/yearlist */
  getYearList(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.baseUrl}master/yearlist`, data, { headers });
  }

  /** POST master/branchlist (legacy alias, use getBranchList in PublicService for new calls) */
  getBranchListLegacy(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}master/branchlist`, data);
  }

  /** POST auction/list */
  getAuctionList(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.baseUrl}auction/list`, data, { headers });
  }

  /** POST api/auction/get-auction */
  getAuctionDetail(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}api/auction/get-auction`, data);
  }

  /** POST bid/update-bid */
  updateBid(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}bid/update-bid`, data);
  }

  /** PATCH bid/update-reserve-price */
  updateReservePriceBid(data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}bid/update-reserve-price`, data);
  }

  /** DELETE vin/delete — remove a VIN image */
  deleteImage(data: any): Observable<any> {
    const httpOptions = { headers: new HttpHeaders({ 'X-Special-Param': 'delete' }), body: data };
    return this.http.delete(`${this.baseUrl}vin/delete`, httpOptions);
  }

  /** POST public/create-ticket — customer query/ticket creation */
  customerQueryCreation(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}common/create-ticket`, data);
  }

  /** GET banner?{queryString} — banner list with raw query string */
  getBannerListRaw(queryString: string): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.get(`${this.baseUrl}banner?${queryString}`, { headers });
  }

  /** POST sell-requests — instant sell request submission */
  createSellRequest(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}sell-requests`, data);
  }

  watchListAdd(data: any) {
    return this.http.post(`${this.apiUrl}inventory/watchlistadd`, data);
  }

  deleteWatchList(data: any) {
    const httpOptions = {
      headers: new HttpHeaders({}), body: data
    }
    return this.http.delete(`${this.apiUrl}inventory/watchlistdelete`, httpOptions);
  }

}
